<template>
  <div
    :class="{
      visible,
      hasDuration: isNumber(duration),
    }"
    data-component-name="AppVideo.Preview"
  >
    <div class="controls">
      <Buttons.Regular aria-label="Play video" @click="$emit('play')">
        <SvgIcon name="play-arrow" />
      </Buttons.Regular>

      <span class="duration nowrap">
        <template v-if="minutes"> {{ minutes }} {{ t('min') }} </template>

        {{ ' ' }}

        <template v-if="seconds"> {{ seconds }} {{ t('sec') }} </template>
      </span>
    </div>

    <div class="overlay" />

    <NuxtImg
      v-if="src"
      :src
      :alt
      :width
      :height
      densities="x1 x2"
      format="webp"
    />
  </div>
</template>

<script setup lang="ts">
// components
import Buttons from '~/components/redesign/Buttons';

// composables
import { useLangT } from '~/composables';

// utils
import isNumber from '~/utils/isNumber';

export type AppVideoPreviewProps = {
  src: string | null;
  alt?: string;

  width?: number;
  height?: number;

  duration?: number;
  visible: boolean;

  lang?: string;
};

const props = withDefaults(defineProps<AppVideoPreviewProps>(), {
  visible: true,
});

defineEmits(['play']);

const t = useLangT(props.lang);

const minutes = computed(() => {
  if (!isNumber(props.duration)) return;

  return Math.floor(props.duration / 60);
});

const seconds = computed(() => {
  if (!isNumber(props.duration) || !isNumber(minutes.value)) return;

  return props.duration - minutes.value * 60;
});
</script>

<style scoped lang="scss">
@import '$/mixins/flex';
@import '$/mixins/size';
@import '$/mixins/typography';
@import '$/mixins/position';
@import '$/functions/token';

[data-component-name='AppVideo.Preview'] {
  opacity: 0;
  transition: opacity 0.15s ease;

  @include absolute-full;

  .controls {
    @include absolute-center;
    z-index: 999999;
    @include flex-stretch-start;
    flex-direction: column;
    transform: translate(-50%, -50%);

    [data-component-name='Buttons.Regular'] {
      transition: transform 0.3s ease;

      &::v-deep([data-component-name='Buttons.Base']) {
        padding: 1rem 2.5rem;

        @include mobile {
          padding: 0.55rem 1.39rem;
        }
      }

      svg {
        @include fixed-size(2.5rem);

        @include mobile {
          @include fixed-size(1.38513rem);
        }
      }
    }

    span.duration {
      @include body-3;
      text-align: center;
      color: token('text-reversed');
      background-color: token('chip-secondary-text');

      position: absolute;
      left: 0;
      right: 0;
      top: 100%;

      transform: translateY(-100%);
      transition: transform 0.25s ease;

      @include mobile {
        font-size: 0.55rem;
        line-height: 1rem;
      }
    }
  }

  &.visible {
    pointer-events: all;
    opacity: 1;
  }

  &.hasDuration {
    .controls {
      [data-component-name='Buttons.Regular'] {
        transform: translateY(-0.8rem);
      }

      span.duration {
        transform: translateY(-0.8rem);
      }
    }
  }

  .overlay {
    @include absolute-full;
    z-index: 10;
    backdrop-filter: blur(10px);
    transition: backdrop-filter 0.15s ease;
  }

  &:hover {
    .overlay {
      backdrop-filter: blur(0);
    }
  }

  img {
    position: relative;
    z-index: 5;
    width: 100%;
    height: 100%;
  }
}
</style>
